import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import OutOfStockSVG from '../../../layouts/svgIcons/OutOfStock';
import WarningIcon from '@mui/icons-material/Warning';
import { Clear } from '@mui/icons-material';
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import { isOperableArray } from '../../../helpers/other';
import { getNotificationAction } from '../Helpers';
import SelectButton from '../../SelectButton';
import { remindMeOptions } from '../Constants';

const NotificationItem = ({ notification, state, handlers }) => {
  const textRef = useRef(null);
  const [isEllipsis, setIsEllipsis] = useState(false);
  const isMessageExpanded = state?.expandedNotifications.find((notificationId) => notificationId === notification.id);

  useEffect(() => {
    const checkOverflow = () => {
      if (textRef.current) {
        setIsEllipsis(textRef.current.scrollWidth > textRef.current.clientWidth);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [notification?.message]);

  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        justifyContent: 'space-between',
        backgroundColor: `var(--locad-notification-banner-default)`,
        color: 'white',
        borderRadius: 1,
        boxShadow: 1,
        height: isMessageExpanded ? 'max-content' : '60px',
        marginBottom: '8px',
        transition: 'height 2s',
        transitionTimingFunction: 'linear',
        [theme.breakpoints.down('sm')]: {
          justifyContent: 'flex-start',
          flexWrap: 'wrap',
          height: 'unset',
        },
      })}
    >
      {/* Section 1 */}
      <Box
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          backgroundColor: `var(--locad-notification-banner-status-default)`,
          color: `var(--locad-notification-banner-default)`,
          padding: '8px',
          minWidth: '200px',
          [theme.breakpoints.down('sm')]: {
            width: '100%',
            justifyContent: 'center',
            alignItems: 'center',
          },
        })}
      >
        <OutOfStockSVG sx={{ fontSize: 20 }} strokeColor={`var(--locad-notification-banner-default)`} />
        <Typography fontSize={14} variant="h6" fontWeight="bold" width={'fit-content'}>
          {notification?.header ?? '-'}
        </Typography>
      </Box>

      {/* Section 2 */}
      <Box
        sx={() => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          padding: '8px',
          overflow: 'hidden',
          overflowY: 'scroll',
        })}
      >
        <WarningIcon fontSize="small" />
        <Typography
          variant="body2"
          sx={{
            whiteSpace: isMessageExpanded ? 'wrap' : 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          ref={textRef}
          data-use-translation={false}
        >
          {notification?.message ?? '-'}
        </Typography>

        {isEllipsis && (
          <Typography
            sx={{ textWrap: 'nowrap', fontSize: '12px', cursor: 'pointer' }}
            onClick={() => handlers.toggleMessageReadMode(notification.id)}
          >
            {isMessageExpanded ? 'Show less' : 'Read more'}
          </Typography>
        )}
      </Box>
      {/* Section 3 (Actions) */}
      <Box
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          padding: '8px',
          whiteSpace: 'nowrap',
          justifyContent: 'flex-end',
          [theme.breakpoints.down('sm')]: {
            width: '100%',
          },
        })}
      >
        {isOperableArray(notification?.actions) &&
          notification?.actions?.map((action) => {
            return getNotificationAction({
              actionType: action?.action_name,
              actionData: {
                ...action?.metadata,
                notificationId: notification.id,
                notificationMessageId: notification.notification_message_id,
              },
              handleAction: handlers.handleAction,
            });
          })}
        <SelectButton
          menuProps={{
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: -65,
            },
          }}
          customTriggerComponent={(props) => (
            <Tooltip title="Remind me">
              <IconButton {...props}>
                <AccessAlarmIcon sx={{ color: 'white' }} />
              </IconButton>
            </Tooltip>
          )}
          buttonProps={{
            sx: {
              background: 'white',
              color: 'var(--locad-notification-banner-default)',
              '&:hover': {
                background: 'white',
                color: 'var(--locad-notification-banner-default)',
              },
            },
          }}
          label="Remind me"
          options={remindMeOptions}
          onSelect={(props) => handlers.remindMe(props, notification.id, notification?.notification_message_id)}
        />
        <IconButton
          disabled={state.isMarkingAsViewed || state.isRemindingMeLater}
          onClick={() =>
            handlers.onCloseNotification({
              notificationId: notification.id,
              messageId: notification?.notification_message_id,
            })
          }
        >
          <Clear sx={{ color: 'white' }} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default NotificationItem;
